import { useEffect, useRef, useState } from 'react';
import { SearchIcon, ImageIcon } from 'components/icons';
import { Spinner } from 'components/ui';
import { useDebounce } from 'hooks/useDebounce';
import { useCatalogSearch } from '../hooks/useCatalogSearch';

/**
 * Search box to find a product and prefill the form. Calls `onSelect(item)`
 * with the chosen result (which carries `vendorCatalogId`).
 */
export function ProductSearch({ onSelect }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounce(query, 300);
  const { results, loading, enabled } = useCatalogSearch(debounced);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const handlePick = (item) => {
    onSelect(item);
    setQuery(item.vendorProductName || item.name || '');
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Search a product to prefill…"
        aria-label="Search products"
        className="h-11 w-full rounded-lg border border-surface-300 bg-white pl-9 pr-9 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
      />
      {loading && (
        <Spinner size="sm" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
      )}

      {open && enabled && (
        <div className="absolute z-40 mt-1 max-h-80 w-full overflow-y-auto rounded-xl border border-surface-200 bg-white py-1 shadow-lg">
          {results.length === 0 && !loading ? (
            <p className="px-3 py-4 text-center text-sm text-slate-500">No products found.</p>
          ) : (
            results.map((item) => {
              const name = item.vendorProductName || item.name || 'Unnamed product';
              return (
                <button
                  key={item.vendorCatalogId}
                  type="button"
                  onClick={() => handlePick(item)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-surface-50"
                >
                  <span className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-surface-100">
                    {item.vendorPrimaryImageUrlThumbnail ? (
                      <img
                        src={item.vendorPrimaryImageUrlThumbnail}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="flex h-full items-center justify-center text-slate-300">
                        <ImageIcon className="h-5 w-5" />
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-900">{name}</span>
                    {item.brand && (
                      <span className="block truncate text-xs text-slate-500">{item.brand}</span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default ProductSearch;
